/**
 * group.ts
 * Secured Communications
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
 */

import { DateTime } from "luxon";

import { PermissionLevel } from "./auth";

export const DB_VERSION = 24;
export const PROTOCOL_VERSION = 2;
export const MIN_PROTOCOL_VERSION = 1;
//export const WELCOME_CTR_CHANGE = 2;

export enum GroupType {
  public = "public",
  private = "private",
  dm = "dm",
  meeting = "meeting"
}

export enum GroupSubmissionType {
  normal = "normal",
  broadcastOnly = "broadcast",
  dropbox = "dropbox"
}

export enum GroupPKI {
  p256 = 1, // ECC based protocol
  crystals = 2  // Post-Quantum Encryption based
}

export enum GroupState {
  active = 1,
  frozen = 20,
  archived = 30,
  deleted = 100
}

export enum ProtocolScheme {
  mls10 = 10,
  mlsSharedSecret = 20
}

export interface GroupSecrets {
  sender_data_secret?: Uint8Array,
  exporter_secret?: Uint8Array,
  authentication_secret?: Uint8Array,
  //external_secret?: Uint8Array,
  confirmation_key?: Uint8Array,
  membership_key?: Uint8Array,
  next_init_secret?: Uint8Array
}
export interface Group extends GroupSecrets {
  _id: string,
  accountID: string,
  groupname: string,
  grouptype: GroupType,
  submissiontype: GroupSubmissionType,
  protocolScheme?: ProtocolScheme,
  groupstate?: GroupState,
  desc: string,
  archiving: boolean,
  pki?: GroupPKI,
  protoVersion: number,
  userHash?: string,

  created?: moment.Moment | Date,

  epoch: number,
  msgCounter: number,

  resumption_secrets?: Array<{epoch: number, secret: Uint8Array}>,
  data_secrets?: Array<{_id: string, secret: Uint8Array}>,
  group_keys?: Array<Uint8Array>,

  externalPubKey?: string,
  externalPrivKey?: string,

  submissionPubKey?: string,
  submissionPriKey?: string,

  treenodes?: Array<RatchetTreeNode>,

  // client-side only
  welcomed?: boolean,
  outOfSync?: boolean,
  unreadMessages?: boolean,
  unreadMentions?: boolean,
  lastMsgTime?: DateTime
}

export interface BsonGroup extends GroupSecrets {
  _id: string,
  accountID?: string,
  groupname: string,
  desc: string,
  archiving: boolean,
  pki: number,
  protoVersion: number,
  userHash?: string,

  created?: Date,

  epoch: number,
  msgCounter: number,

  resumption_secrets?: Array<{epoch: number, secret: ArrayBuffer}>,
  group_keys?: Array<Uint8Array>,

  externalPubKey?: string,
  externalPrivKey?: string,

  treenodes?: Array<BsonRatchetTreeNode>
}

export interface Ratchet {
  ratchet_secret?: ArrayBuffer,
  handshake_secret?: ArrayBuffer
}

export interface RatchetTreeNode extends Ratchet {
  userID?: string,
  deviceID?: string,
  nodeHash?: Uint8Array, // hashes index, publicKey, and children hashes

  privateKey?: string,
  publicKey?: string,
  signKey?: string, // derived, dilithium private
  verifyKey?: string, // derived, dilithium public

  generation: number,
  handshakeGeneration: number

  index?: number  // temporary, stores this node's cur index in ratchet tree
}

export interface BsonRatchetTreeNode extends Ratchet {
  userID?: string,
  deviceID?: string,
  nodeHash?: Uint8Array,

  privateKey?: string,
  publicKey?: string,
  signKey?: string,
  verifyKey?: string,

  generation: number,
  handshakeGeneration: number
}

export enum GroupMemberType {
  owner = "owner",
  manager = "manager",
  user = "user",
  observer = "observer"
}

export enum MemberNotifyOptions {
  none = "none",
  mentions = "mentions",
  all = "all"
}

export interface GroupScopes {
  info?: PermissionLevel,
  members?: PermissionLevel,
  messages?: PermissionLevel,
  files?: PermissionLevel,
  invites?: PermissionLevel,
  huddles?: PermissionLevel
}

export interface DeviceMembership {
  _id: string,
  atEpoch: number,
  atCounter: number
}

export interface GroupMembership {
  accountID: string,
  groupID: string,
  userID: string,
  groupname: string,
  grouptype: GroupType,
  submissiontype: GroupSubmissionType,
  protocolScheme?: ProtocolScheme,
  groupstate?: GroupState,
  groupMemberType: GroupMemberType,
  notifyOn?: MemberNotifyOptions,
  archiving: boolean,
  perms: GroupScopes,
  devices: Array<DeviceMembership>,
  created: moment.Moment,
  ongoingHuddle?: boolean,

  // client-side only
  haveRatchetTree?: boolean,
  isHovering?: boolean,
  outOfSync?: boolean,
  unreadMessages?: boolean,
  unreadMentions?: boolean,
  needArchiving?: boolean,
  fullyWelcomed?: boolean,
  lastMsgTime?: DateTime
}

export interface Keys {
  publicKey: string,
  privateKey: string,
  kyberPublicKey: string,
  kyberPrivateKey: string,
  diPublicKey: string,
  diPrivateKey: string
}

export interface StoredKeys extends Keys {
  deviceID: string,
  created: moment.Moment
}
